import _get from 'lodash/get'
import React from 'react'
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { tv } from 'tailwind-variants'
import type { ReadonlyDeep } from 'type-fest'
import type { ZodTypeAny } from 'zod'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { stripEmptyListEntries } from '@flows-ui/utils/stripEmptyListEntries'
import type { TextContent } from '@flows/core/i18n/types'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import { evaluateCondition } from '@flows/renderer/utils/conditions'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@ui-kit/components/Button'
import { Popup } from '@ui-kit/components/Popup'

interface Props {
  visible: boolean
  onSubmit: (arg1: Record<string, any>) => void
  onClose: () => void
  remove: () => void
  isEdit: boolean
  defaultValues?: Record<string, any>
  activeIndex: number
  renderContent: (popupFormValues: Record<string, any>) => React.ReactNode
  title: TextContent
  schema: ReadonlyDeep<ZodTypeAny>
  elements: readonly ReadonlyDeep<NonNullable<AnyStepContentElement>>[]
}

const listPopUpStyles = tv({
  slots: {
    childrenContainer: 'flex flex-col gap-y-2',
    buttonContainer: 'gap-y-4 pb-4',
  },
})

const PopupContent = ({
  renderContent,
  className,
}: {
  renderContent: (popupFormValues: Record<string, any>) => React.ReactNode
  className: string
}) => {
  const popupFormValues = useWatch()
  return <View className={className}>{renderContent(popupFormValues ?? {})}</View>
}

export const ListPopup = (props: Props) => {
  const { t } = useTranslation()
  const { watch } = useFormContext()
  const flowValues = watch()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues,
    servicesResponses,
  })

  const { buttonContainer, childrenContainer } = listPopUpStyles()

  const hasZodSchema = typeof (props.schema as { safeParse?: unknown })?.safeParse === 'function'
  const form = useForm({
    defaultValues: props.defaultValues,
    resolver: hasZodSchema ? zodResolver(props.schema) : undefined,
  })

  const popupValues = useWatch({ control: form.control })
  const visibleValues = { ...flowValues, ...popupValues }
  const hasEveryField = props.elements.every((element) => {
    if (!('name' in element) || typeof element.name !== 'string') {
      return true
    }
    if ('condition' in element && element.condition) {
      if (!evaluateCondition(element.condition, visibleValues)) {
        return true
      }
    }
    const value = _get(popupValues, element.name)
    return value !== undefined && value !== null && value !== ''
  })

  const onSubmit = (values: Record<string, any>) => {
    props.onSubmit(stripEmptyListEntries(values))
  }

  const { handleSubmit } = form

  const handleFormSubmit = () => {
    return handleSubmit(onSubmit)()
  }

  const handleRemove = () => {
    return props.remove()
  }

  return (
    <Popup
      visible={props.visible}
      onRequestClose={props.onClose}
      title={translateFunction({
        ...props.title,
        values: {
          index: {
            type: 'staticValue',
            value: (props.activeIndex + 1).toString(),
          },
        },
      })}
      isFullHeight
    >
      <FormProvider {...form}>
        <PopupContent renderContent={props.renderContent} className={childrenContainer()} />
      </FormProvider>
      <View className={buttonContainer()}>
        <Button
          variantColor="black"
          label={t('editableList.saveButton')}
          onPress={handleFormSubmit}
          disabled={!hasEveryField}
        />
        {props.isEdit ? (
          <Button
            type="text"
            variantColor="error"
            label={t('editableList.removeButton')}
            onPress={handleRemove}
          />
        ) : null}
      </View>
    </Popup>
  )
}
